import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { PagedResult } from '../../../core/model/paged-result';
import { MessageService } from '../../../core/service/message.service';
import {
  ProtectedIdentityContact,
  ProtectedIdentityContactForm,
  ProtectedIdentityAccess,
  IdentityLog,
  IdentityLogPageRequest,
} from '../../model/protected-identity';

@Injectable()
export class ProtectedIdentityService {
  private readonly endpoint: string;
  private readonly contactsEndpoint: string;

  constructor(
    private readonly client: HttpClient,
    @Inject(APP_CONFIG) config: IAppConfig,
    private readonly messageService: MessageService
  ) {
    this.endpoint = `${config.apiEndpointV2}/protected-identity`;
    this.contactsEndpoint = `${this.endpoint}/contacts`;
  }

  get(contactId: string): Observable<ProtectedIdentityContact> {
    return this.handle(this.client.get<ProtectedIdentityContact>(`${this.contactsEndpoint}/${contactId}`));
  }

  getAccess(showError = true): Observable<ProtectedIdentityAccess> {
    return this.handle(this.client.get<ProtectedIdentityAccess>(`${this.endpoint}/access`), showError);
  }

  verifyBankId(orderReference: string): Observable<ProtectedIdentityAccess> {
    return this.handle(
      this.client.post<ProtectedIdentityAccess>(`${this.endpoint}/access/bankid`, { orderReference })
    );
  }

  extendAccess(): Observable<ProtectedIdentityAccess> {
    return this.handle(this.client.post<ProtectedIdentityAccess>(`${this.endpoint}/access/extend`, {}));
  }

  endAccess(): Observable<void> {
    return this.handle(this.client.post<void>(`${this.endpoint}/access/end`, {}));
  }

  getLogs(contactId: string, request: IdentityLogPageRequest): Observable<PagedResult<IdentityLog>> {
    return this.handle(
      this.client.post<PagedResult<IdentityLog>>(`${this.contactsEndpoint}/${contactId}/logs/page`, request).pipe(
        map(result => {
          const page = Object.assign(new PagedResult<IdentityLog>(), result);
          page.items = result.items.map(log =>
            Object.assign(new IdentityLog(), log, {
              eventTranslation: `PROTECTED_IDENTITY.EVENT.${log.eventType}`,
            })
          );
          return page;
        })
      )
    );
  }

  activate(contactId: string, form: ProtectedIdentityContactForm): Observable<ProtectedIdentityContact> {
    return this.handle(this.client.post<ProtectedIdentityContact>(`${this.contactsEndpoint}/${contactId}/activate`, form));
  }

  update(contactId: string, form: ProtectedIdentityContactForm): Observable<ProtectedIdentityContact> {
    return this.handle(this.client.put<ProtectedIdentityContact>(`${this.contactsEndpoint}/${contactId}`, form));
  }

  deactivate(contactId: string): Observable<void> {
    return this.handle(this.client.post<void>(`${this.contactsEndpoint}/${contactId}/deactivate`, {}));
  }

  private handle<T>(request: Observable<T>, showError = true): Observable<T> {
    return request.pipe(
      catchError(error => {
        // Never pass the complete HttpErrorResponse to the global MessageService: it writes its
        // argument to the browser console and the response may belong to a sensitive endpoint.
        // The API contract exposes only a sanitized public message for these requests.
        if (showError) this.messageService.showError(this.getPublicErrorMessage(error));
        return throwError(() => error);
      })
    );
  }

  private getPublicErrorMessage(error: any): string {
    const publicMessage = error?.error?.message;
    return typeof publicMessage === 'string' && publicMessage.length > 0 ? publicMessage : 'ERROR.UNKNOWN';
  }
}
